import moment from 'moment';
import { OnlyFansMassDMMessageScheduleDocumentWithColor } from '../hooks/useMessageUsersMassDmList';

interface MassDmOccurrence {
    repost: OnlyFansMassDMMessageScheduleDocumentWithColor;
    scheduledTime: moment.Moment;
}

export function calculateUpcomingMassDmsSchedule(
    massDmsSchedule: OnlyFansMassDMMessageScheduleDocumentWithColor[],
    fromTime: moment.Moment = moment(),
): MassDmOccurrence[] {
    const upcomingReposts: MassDmOccurrence[] = [];
    const endTime = moment(fromTime).add(24, 'hours');

    massDmsSchedule.forEach(massDmSchedule => {
        if (!massDmSchedule.active || massDmSchedule.inactive || !massDmSchedule.scheduleDate) return;

        const scheduleDate = moment(massDmSchedule.scheduleDate);

        // If this is a one-time message (no repeat)
        if (!massDmSchedule.repeatEveryAmount || !massDmSchedule.repeatEveryUnit) {
            // Only add if it's within our time window
            if (scheduleDate.isSameOrAfter(fromTime) && scheduleDate.isSameOrBefore(endTime)) {
                upcomingReposts.push({
                    repost: massDmSchedule,
                    scheduledTime: moment(scheduleDate),
                });
            }
            return;
        }

        // For repeating messages, find ALL occurrences within the 24-hour window
        const intervalDuration = moment.duration(massDmSchedule.repeatEveryAmount, massDmSchedule.repeatEveryUnit);
        const intervalMs = intervalDuration.asMilliseconds();

        if (intervalMs <= 0) return; // Invalid interval

        // Start from the original schedule date
        let currentOccurrence = moment(scheduleDate);
        const maxOccurrences = 2000; // Safety limit to prevent infinite loops
        let addedCount = 0;

        while (addedCount < maxOccurrences) {
            // Check if we've passed the repeat end date
            if (massDmSchedule.repeatEndDate && currentOccurrence.isAfter(moment(massDmSchedule.repeatEndDate))) {
                break;
            }

            // If this occurrence is beyond our 24-hour window, we're done
            if (currentOccurrence.isAfter(endTime)) {
                break;
            }

            // Add this occurrence if it's within our time window (from now to 24 hours)
            if (currentOccurrence.isSameOrAfter(fromTime)) {
                upcomingReposts.push({
                    repost: massDmSchedule,
                    scheduledTime: moment(currentOccurrence),
                });
                addedCount++;
            }

            // Move to the next occurrence
            currentOccurrence = moment(currentOccurrence).add(massDmSchedule.repeatEveryAmount, massDmSchedule.repeatEveryUnit);
        }
    });

    // Sort by scheduled time
    return upcomingReposts.sort((a, b) => a.scheduledTime.valueOf() - b.scheduledTime.valueOf());
}

// Helper function to get just the count if that's all you need
export function getUpcomingMassDmsScheduleCount(
    reposts: OnlyFansMassDMMessageScheduleDocumentWithColor[],
    fromTime: moment.Moment = moment(),
): number {
    return calculateUpcomingMassDmsSchedule(reposts, fromTime).length;
}
